import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useDataProvider, useLocale, useNotify, useTranslate } from "ra-core";
import { useLocation } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { ChoiceChips } from "../design/ChoiceChips";

import type { CrmDataProvider } from "../providers/types";
import type { FeedbackKind, FeedbackReportInput } from "../types";
import { FEEDBACK_KINDS } from "../types";
import { deriveClientContext, deriveScreenContext } from "./feedbackContext";
import {
  FEEDBACK_MESSAGE_MAX,
  FeedbackError,
  validateFeedbackMessage,
} from "./feedbackRules";

const MESSAGE_FIELD_ID = "feedback-message";
const ERROR_ID = "feedback-error";

/**
 * Almikia Epic PL (TASK-061): the "Reportar un problema o sugerencia" dialog.
 * Mounted only while open, so the button adds no work (and no request) until
 * it is used. The screen context is captured once, when the dialog opens, so
 * the report carries the title of the screen the author was looking at.
 * Nothing is read back after the insert (the author has no SELECT policy).
 */
export const FeedbackDialog = ({ onClose }: { onClose: () => void }) => {
  const translate = useTranslate();
  const locale = useLocale();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { pathname } = useLocation();

  const [screen] = useState(() => deriveScreenContext(pathname, translate));
  const [kind, setKind] = useState<FeedbackKind>("problema");
  const [message, setMessage] = useState("");
  const [errorText, setErrorText] = useState<string | null>(null);

  const { mutate, isPending } = useMutation({
    mutationKey: ["feedback-report"],
    mutationFn: (input: FeedbackReportInput) =>
      dataProvider.create("feedback_reports", { data: input }),
    onSuccess: () => {
      notify("crm.feedback.success", {
        type: "success",
        messageArgs: { _: "Gracias, tu comentario llegó a Dirección" },
      });
      onClose();
    },
    // Keeps the typed text: only the error line changes.
    onError: (error) => {
      setErrorText(
        error instanceof FeedbackError
          ? error.message
          : translate("crm.feedback.error_generic"),
      );
    },
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isPending) return;
    const validation = validateFeedbackMessage(message);
    if (validation === "empty") {
      setErrorText(translate("crm.feedback.error_empty"));
      return;
    }
    if (validation === "too_long") {
      setErrorText(
        translate("crm.feedback.error_too_long", { max: FEEDBACK_MESSAGE_MAX }),
      );
      return;
    }
    setErrorText(null);
    mutate({
      kind,
      message: message.trim(),
      ...screen,
      ...deriveClientContext({
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        locale,
        userAgent: window.navigator.userAgent,
      }),
    });
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        // A request in flight cannot be abandoned half-way.
        if (!open && !isPending) onClose();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit} noValidate className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{translate("crm.feedback.title")}</DialogTitle>
            <DialogDescription>
              {translate("crm.feedback.description")}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-2">
            <span className="text-sm font-medium" aria-hidden="true">
              {translate("crm.feedback.kind_label")}
            </span>
            <ChoiceChips
              label={translate("crm.feedback.kind_label")}
              mode="single"
              value={[kind]}
              options={FEEDBACK_KINDS.map((option) => ({
                id: option,
                label: translate(`crm.feedback.kind_${option}`),
                pending: isPending || undefined,
              }))}
              onChange={(next) => {
                // Single mode reports [] when the selected chip is tapped
                // again: a report always has a kind, so keep the current one.
                const [picked] = next;
                if (picked) setKind(picked as FeedbackKind);
              }}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor={MESSAGE_FIELD_ID}>
              {translate("crm.feedback.message_label")}
            </Label>
            <Textarea
              id={MESSAGE_FIELD_ID}
              value={message}
              rows={6}
              disabled={isPending}
              placeholder={translate("crm.feedback.message_placeholder")}
              aria-invalid={errorText != null}
              aria-describedby={errorText ? ERROR_ID : undefined}
              onChange={(event) => setMessage(event.target.value)}
            />
          </div>

          <p className="text-sm text-muted-foreground">
            {translate("crm.feedback.screen", { title: screen.screen_title })}
          </p>

          {errorText ? (
            <p id={ERROR_ID} role="alert" className="text-sm text-destructive">
              {errorText}
            </p>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isPending}
              onClick={onClose}
            >
              {translate("crm.feedback.cancel")}
            </Button>
            <Button type="submit" disabled={isPending} aria-busy={isPending}>
              {translate(
                isPending ? "crm.feedback.sending" : "crm.feedback.send",
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
